import React from 'react';
// Type-only so this component never drags the cache slice (and its API/oidc
// dependency chain) into consumers' module graphs.
import type { CacheLookup } from '../../app/cacheSlice';
import { isSystemRole } from '../../constants';
import { TransferList } from '@lp/lp-ui';

export type RoleSelectorProps = {
    roles: CacheLookup[],
    selectedIds: number[],
    /** Receives the complete next selection, so the component owns every mutation. */
    onChange: (nextIds: number[]) => void,
    disabled?: boolean,
    height?: number | string,
    namePrefix?: string
};

// Same ordering as the Roles page: system roles first, then alphabetical.
const bySystemThenName = (a: CacheLookup, b: CacheLookup) => {
    const aSystem = isSystemRole(a.label) ? 0 : 1;
    const bSystem = isSystemRole(b.label) ? 0 : 1;
    return aSystem - bSystem || a.label.localeCompare(b.label);
};

const ROLE_NOUNS = { singular: 'role', plural: 'roles' };

/**
 * The role-flavoured TransferList: supplies the Roles page's ordering and the role wording.
 * Kept as the single place that configuration lives, rather than repeating it at each of the
 * nine call sites.
 *
 * Passes `selectedIds` through untouched: an id with no row here is one the caller may not be
 * permitted to see, and dropping it reads to the API as a removal - which the SuperAdmin guard
 * answers with a 403 that then blocks every later save for that user.
 */
const RoleSelector: React.FC<RoleSelectorProps> = ({
    roles,
    selectedIds,
    onChange,
    disabled = false,
    height = 560,
    namePrefix = 'roles'
}: RoleSelectorProps) => (
    <TransferList
        items={roles}
        selectedIds={selectedIds}
        onChange={onChange}
        entityName={ROLE_NOUNS}
        compare={bySystemThenName}
        disabled={disabled}
        height={height}
        namePrefix={namePrefix}
    />
);

export default RoleSelector;
